/**
 * LaTeX → flattened handwriting text (the "Kalam" approach).
 *
 * The board is hand-drawn, so a SIMPLE formula should read like the teacher
 * wrote it and write-on animate with his voice — not pop in as a typeset KaTeX
 * block. This flattens tex to inline Unicode text (fractions → a/b, sqrt →
 * √(...), powers/subscripts → Unicode superscript/subscript glyphs, symbols →
 * Unicode) so the ordinary handwriting engine can draw it.
 *
 * HYBRID CONTRACT — this is a *lossy* transform and it says so. Anything it
 * cannot represent faithfully (integrals, sums, matrices, accents like \vec,
 * multi-character or non-mappable exponents, or any unknown command) returns
 * `{ lossy: true }`, and the caller renders real KaTeX instead. We NEVER
 * silently drop math — that is the trap in the naive version of this approach
 * and it would bite the moment calculus/integration content shows up.
 */

const SUP: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶',
  '7': '⁷', '8': '⁸', '9': '⁹', '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽',
  ')': '⁾', n: 'ⁿ', i: 'ⁱ',
};
const SUB: Record<string, string> = {
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆',
  '7': '₇', '8': '₈', '9': '₉', '+': '₊', '-': '₋', '=': '₌', '(': '₍',
  ')': '₎', a: 'ₐ', e: 'ₑ', o: 'ₒ', x: 'ₓ', h: 'ₕ', k: 'ₖ', l: 'ₗ',
  m: 'ₘ', n: 'ₙ', p: 'ₚ', s: 'ₛ', t: 'ₜ', i: 'ᵢ', j: 'ⱼ', r: 'ᵣ', u: 'ᵤ', v: 'ᵥ',
};

// Constructs we will NOT fake as inline text — hand them to KaTeX instead.
const BAIL =
  /\\(int|iint|iiint|oint|sum|prod|coprod|lim|limsup|liminf|begin|end|matrix|pmatrix|bmatrix|vmatrix|cases|array|vec|hat|tilde|bar|dot|ddot|overline|underline|overrightarrow|overleftarrow|binom|substack|middle)\b|\\\\|&/;

/** Map every char of a super/subscript operand, or return null if any can't. */
function mapScript(group: string, table: Record<string, string>): string | null {
  let out = '';
  for (const ch of group) {
    if (ch in table) out += table[ch];
    else return null; // unmappable → caller marks lossy
  }
  return out;
}

export function texToKalam(tex: string): { text: string; lossy: boolean } {
  const src = String(tex ?? '');
  if (!src.trim()) return { text: '', lossy: false };
  if (BAIL.test(src)) return { text: '', lossy: true };

  let lossy = false;
  const takeBrace = (str: string, i: number): [string, number] => {
    let d = 0, j = i;
    for (; j < str.length; j++) {
      if (str[j] === '{') d++;
      else if (str[j] === '}' && !--d) break;
    }
    return [str.slice(i + 1, j), j];
  };

  // \frac{A}{B} → A/B, parenthesising compound operands, recursively.
  const frac = (str: string): string => {
    let out = '', i = 0;
    while (i < str.length) {
      const m = str.slice(i).match(/^\\[dt]?frac\s*/);
      if (m && str[i + m[0].length] === '{') {
        const [num, e1] = takeBrace(str, i + m[0].length);
        let k = e1 + 1;
        while (str[k] === ' ') k++;
        if (str[k] === '{') {
          const [den, e2] = takeBrace(str, k);
          // Parenthesise ANY compound operand, not just +/-. A numerator like
          // "4 × 3/5" (multiplication, or a NESTED fraction) flattened to
          // "4 × 3/5/5" — mathematically ambiguous on a student's board
          // (user-reported 2026-08-21). Wrap whenever an operator survives the
          // flatten, so nested fractions read as (4 × 3/5)/5.
          const wrap = (x: string) => {
            const flat = frac(x);
            return /[+\-×·÷\/]/.test(flat) ? `(${flat})` : flat;
          };
          out += `${wrap(num)}/${wrap(den)}`;
          i = e2 + 1;
          continue;
        }
      }
      out += str[i++];
    }
    return out;
  };

  // Superscripts / subscripts → Unicode (or mark lossy if an operand can't map).
  const scripts = (str: string): string => {
    let out = '', i = 0;
    while (i < str.length) {
      const c = str[i];
      if ((c === '^' || c === '_') && i + 1 < str.length) {
        const table = c === '^' ? SUP : SUB;
        let group: string, next: number;
        if (str[i + 1] === '{') {
          const [g, e] = takeBrace(str, i + 1);
          group = g;
          next = e + 1;
        } else {
          group = str[i + 1];
          next = i + 2;
        }
        const mapped = mapScript(group, table);
        if (mapped == null) {
          lossy = true;
          return str; // bail early; KaTeX will handle the whole thing
        }
        out += mapped;
        i = next;
      } else {
        out += c;
        i += 1;
      }
    }
    return out;
  };

  let s = frac(src);
  // Degree / prime BEFORE generic ^ mapping (they are special super-scripts).
  s = s
    .replace(/\^\s*\{?\s*\\circ\s*\}?/g, '°')
    .replace(/\^\s*\{?\s*\\prime\\prime\s*\}?/g, '″')
    .replace(/\^\s*\{?\s*\\prime\s*\}?/g, '′')
    .replace(/\\sqrt\s*\{([^}]*)\}/g, '√($1)')
    .replace(/\\text\s*\{([^}]*)\}/g, '$1')
    .replace(/\\mathrm\s*\{([^}]*)\}/g, '$1')
    .replace(/\\operatorname\s*\{([^}]*)\}/g, '$1');
  s = scripts(s);
  if (lossy) return { text: '', lossy: true };

  s = s
    // Delimiters + comparison operators FIRST, while the separating backslash
    // is still there: `\le\sin` must map `\le` before `\sin`→"sin" glues them.
    .replace(/\\left|\\right/g, '')
    .replace(/\\leq/g, '≤').replace(/\\geq/g, '≥')
    .replace(/\\le(?!ft)/g, '≤').replace(/\\ge(?!q)/g, '≥') // \le/\ge aliases (not \left)
    // Operator names render upright — "tan θ" is exactly how it's handwritten.
    // (The naive version STRIPPED these, turning "tan θ" into "θ" — silent, wrong.)
    .replace(
      /\\(arcsin|arccos|arctan|sinh|cosh|tanh|sin|cos|tan|csc|sec|cot|log|ln|exp|deg|max|min|det|gcd|dim)(?![a-zA-Z])/g,
      '$1',
    )
    .replace(/\\q?quad/g, '  ') // \quad / \qquad → wide space
    .replace(/\\times/g, '×').replace(/\\cdot/g, '·').replace(/\\div/g, '÷').replace(/\\pm/g, '±')
    .replace(/\\Delta/g, 'Δ').replace(/\\delta/g, 'δ').replace(/\\pi/g, 'π').replace(/\\theta/g, 'θ')
    .replace(/\\rho/g, 'ρ').replace(/\\omega/g, 'ω').replace(/\\alpha/g, 'α').replace(/\\beta/g, 'β')
    .replace(/\\lambda/g, 'λ').replace(/\\mu/g, 'µ').replace(/\\gamma/g, 'γ').replace(/\\phi/g, 'φ')
    .replace(/\\Omega/g, 'Ω').replace(/\\approx/g, '≈').replace(/\\neq/g, '≠')
    .replace(/\\(?:rightarrow|to)/g, '→').replace(/\\Rightarrow/g, '⇒')
    .replace(/\\propto/g, '∝').replace(/\\infty/g, '∞').replace(/\\circ/g, '°').replace(/\\degree/g, '°')
    .replace(/\\[,!;:>\s]/g, ' ')
    .replace(/~/g, ' ');

  // Any command still standing is something we don't understand → KaTeX.
  if (/\\[a-zA-Z]+/.test(s)) return { text: '', lossy: true };

  s = s
    .replace(/[{}$]/g, '')
    .replace(/\s*([=×·÷±≈≤≥→⇒∝<>])\s*/g, ' $1 ') // breathe around operators
    .replace(/\s+/g, ' ')
    .trim();

  // RAW text (real <, >, &) — the admin handwriting renderer takes plain text.
  // The player path wraps this in a <span> and must escape it there itself.
  return { text: s, lossy: false };
}
